(() => {
  'use strict';
  const core = globalThis.MBLSeasonalCore;
  if (!core) return;
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = String(text);
    return element;
  };
  const read = root => {
    try { const data = JSON.parse(root.querySelector('[data-seasonal-data]').content.textContent); return Array.isArray(data.campaigns) && data.context ? data : null; }
    catch { return null; }
  };
  const hide = (element, value) => { if (element && element.hidden !== value) element.hidden = value; };
  const visible = element => {
    const rect = element.getBoundingClientRect(), style = getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  };
  function safeURL(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try { const url = new URL(value, location.origin); return ['http:', 'https:'].includes(url.protocol) ? url.href : null; }
    catch { return null; }
  }
  function destination(campaign, root) {
    if (typeof campaign.url !== 'string' || !campaign.url) return null;
    let url = campaign.url;
    if (campaign.resourceDestination && url.startsWith('/') && !url.startsWith('//') && root !== '/' && !url.startsWith(root)) url = root.replace(/\/$/, '') + '/' + url.replace(/^\//, '');
    return safeURL(url);
  }
  function colors(element, campaign) {
    for (const [key, value, fallback] of [['accent', campaign.accent, '#a85432'], ['background', campaign.background, '#ffffff'], ['color', campaign.color, '#25241f']]) {
      element.style.setProperty('--campaign-' + key, typeof value === 'string' && /^#[a-f0-9]{6}$/i.test(value) ? value : fallback);
    }
  }
  function picture(campaign) {
    if (!campaign.image || !safeURL(campaign.image.src)) return null;
    const frame = node('picture', 'mbl-campaign-picture');
    if (campaign.mobileImage && safeURL(campaign.mobileImage.src)) {
      const source = node('source'); source.media = '(max-width:600px)';
      source.srcset = (safeURL(campaign.mobileImage.small) || safeURL(campaign.mobileImage.src)) + ' 480w, ' + safeURL(campaign.mobileImage.src) + ' 900w'; frame.append(source);
    }
    const image = node('img'); image.src = safeURL(campaign.image.src);
    image.srcset = [480, 900, 1400].map((width, index) => {
      const url = safeURL([campaign.image.small, campaign.image.medium, campaign.image.src][index]);
      return url ? url + ' ' + width + 'w' : null;
    }).filter(Boolean).join(', ');
    image.sizes = '(max-width:600px) calc(100vw - 48px), 380px'; image.alt = campaign.image.alt || campaign.heading || '';
    if (campaign.image.width > 0) image.width = campaign.image.width;
    if (campaign.image.height > 0) image.height = campaign.image.height;
    image.loading = 'lazy'; frame.append(image); return frame;
  }
  function content(campaign, root, headingId) {
    const fragment = document.createDocumentFragment(), media = picture(campaign);
    if (media) fragment.append(media);
    const copy = node('div', 'mbl-campaign-copy'), title = node('h2', '', campaign.heading);
    if (headingId) title.id = headingId;
    copy.append(title); if (campaign.message) copy.append(node('p', '', campaign.message));
    const url = destination(campaign, root);
    if (campaign.label && url) { const link = node('a', 'button', campaign.label); link.href = url; copy.append(link); }
    fragment.append(copy); return fragment;
  }
  let manager;
  function start(root) {
    let data = read(root);
    if (!data) return () => {};
    const editor = root.dataset.editor === 'true' || !!globalThis.Shopify?.designMode;
    if (editor) data.context = {...data.context, editor: true, previewId: data.previewId};
    let storage = !editor;
    if (!editor) { try { sessionStorage.setItem('mbl-seasonal-probe', '1'); sessionStorage.removeItem('mbl-seasonal-probe'); } catch { storage = false; } }
    const seen = new Set(), dialog = root.querySelector('[data-seasonal-dialog]'), popupContent = root.querySelector('[data-seasonal-popup-content]');
    const announcement = root.querySelector('[data-seasonal-announcement]') || node('div', 'mbl-campaign-announcement');
    announcement.dataset.seasonalAnnouncement = '';
    hide(announcement, true);
    const header = document.querySelector('.shopify-section:has(.site-header)'), base = header?.querySelector('.atlas-announcement');
    const originalBaseHidden = base?.hidden;
    if (header) header.prepend(announcement);
    let announcementKey = null, announcementFingerprint = null, popupKey = null, pendingKey = null, readyAt = 0, priorFocus;
    let delayTimer, mutationTimer, controller, destroyed = false;
    const banners = new WeakMap();
    const hasSeen = campaign => {
      const key = core.sessionKey(campaign);
      if (seen.has(key)) return true;
      if (editor) return false;
      if (!storage) return true;
      try { return sessionStorage.getItem(key) === 'shown'; } catch { storage = false; return true; }
    };
    const markVisible = (campaign, element) => requestAnimationFrame(() => {
      if (destroyed || document.hidden || !element.isConnected || !visible(element)) return;
      const key = core.sessionKey(campaign); seen.add(key);
      if (!editor && storage) { try { sessionStorage.setItem(key, 'shown'); } catch { storage = false; check(); } }
    });
    const blocking = () => [...document.querySelectorAll('dialog[open],[aria-modal="true"],[role="dialog"],[data-popup],[data-cookie-banner],#shopify-pc__banner,.atlas-chat-panel:not([hidden])')].some(element => element !== dialog && !dialog.contains(element) && visible(element));
    const restore = () => { if (!blocking() && priorFocus?.isConnected) priorFocus.focus(); popupKey = null; };
    dialog.addEventListener('close', restore);
    const close = event => { if (event.target.closest('[data-seasonal-close]')) dialog.close(); };
    dialog.addEventListener('click', close);
    const backdrop = event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    };
    dialog.addEventListener('click', backdrop);
    function check() {
      if (destroyed) return;
      const campaigns = data.enabled === true && data.complete !== false ? data.campaigns : [], now = Date.now();
      const bar = core.winner(campaigns, data.context, now, 'announcement'), key = bar && core.sessionKey(bar);
      if (!bar) {
        hide(announcement, true); if (base) hide(base, !!originalBaseHidden); announcementKey = null; announcementFingerprint = null;
      } else if (key !== announcementKey || JSON.stringify(bar) !== announcementFingerprint) {
        const news = node('div', 'mbl-campaign-news'), copy = node('div', 'mbl-campaign-news-copy');
        const gift = node('i'); gift.dataset.lucide = 'gift'; gift.setAttribute('aria-hidden', 'true'); news.append(gift);
        copy.append(node('strong', 'mbl-campaign-news-title', bar.heading));
        if (bar.message) copy.append(node('span', 'mbl-campaign-news-message', bar.message));
        news.append(copy); announcement.replaceChildren(news);
        const url = destination(bar, data.root);
        if (bar.label && url) { const link = node('a', 'mbl-campaign-news-link', bar.label); link.href = url; announcement.append(link); }
        globalThis.lucide?.createIcons(); colors(announcement, bar); announcementKey = key; announcementFingerprint = JSON.stringify(bar);
        if (base) hide(base, true); hide(announcement, false);
      }
      const placements = new Set();
      document.querySelectorAll('[data-seasonal-placement]').forEach(element => {
        const placement = element.dataset.seasonalPlacement || 'main';
        const campaign = placements.has(placement) ? null : core.winner(campaigns, data.context, now, 'banner', placement);
        placements.add(placement);
        if (!campaign) { hide(element, true); banners.delete(element); return; }
        const fingerprint = JSON.stringify(campaign);
        if (banners.get(element) !== fingerprint) { element.querySelector('[data-seasonal-banner-content]').replaceChildren(content(campaign, data.root)); colors(element, campaign); banners.set(element, fingerprint); }
        hide(element, false);
      });
      const popup = core.winner(campaigns, data.context, now, 'popup'), nextKey = popup && core.sessionKey(popup);
      if (dialog.open && (nextKey !== popupKey || blocking())) dialog.close();
      if (!popup || (!editor && !storage) || (popupKey !== nextKey && hasSeen(popup))) { clearTimeout(delayTimer); pendingKey = null; return; }
      if (dialog.open || document.hidden) return;
      if (pendingKey !== nextKey) { pendingKey = nextKey; readyAt = Date.now() + Math.min(30, Math.max(0, Number(popup.delay) || 0)) * 1000; }
      const remaining = readyAt - Date.now();
      clearTimeout(delayTimer);
      if (remaining > 0) { delayTimer = setTimeout(check, remaining); return; }
      if (blocking()) return;
      colors(dialog, popup); popupContent.replaceChildren(content(popup, data.root, dialog.getAttribute('aria-labelledby')));
      priorFocus = document.activeElement;
      try { dialog.showModal(); popupKey = nextKey; dialog.querySelector('[data-seasonal-close]').focus(); markVisible(popup, dialog); }
      catch { popupKey = null; }
    }
    async function refresh() {
      if ((editor && data.complete !== false) || destroyed || document.hidden) return;
      controller?.abort(); controller = new AbortController();
      const activeRequest = controller;
      try {
        const url = new URL(location.href); url.searchParams.set('sections', root.dataset.sectionId);
        const parameter = data.pagination?.param || 'page'; url.searchParams.set(parameter, '1');
        const response = await fetch(url, {signal:activeRequest.signal, cache:'no-store', headers:{Accept:'application/json'}});
        if (!response.ok) return;
        const sections = await response.json(), html = sections[root.dataset.sectionId];
        if (typeof html !== 'string') return;
        const nextRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-seasonal-root]'), next = nextRoot && read(nextRoot);
        if (!next) return;
        const pages = Math.max(1, Number(next.pagination?.pages) || 1);
        if (pages > 10) { data = {...next,enabled:false}; check(); return; }
        const campaigns = [...next.campaigns];
        for (let page = 2; page <= pages; page++) {
          url.searchParams.set(next.pagination?.param || parameter, String(page));
          const response = await fetch(url, {signal:activeRequest.signal,cache:'no-store',headers:{Accept:'application/json'}});
          if (!response.ok) return;
          const sections = await response.json(), html = sections[root.dataset.sectionId];
          if (typeof html !== 'string') return;
          const pageRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-seasonal-root]'), pageData = pageRoot && read(pageRoot);
          if (!pageData) return;
          campaigns.push(...pageData.campaigns);
        }
        if (!destroyed && activeRequest === controller && !activeRequest.signal.aborted) { data = {...next,context:editor ? {...next.context,editor:true,previewId:next.previewId} : next.context,complete:true,campaigns:[...new Map(campaigns.map(campaign => [campaign.id,campaign])).values()]}; check(); }
      } catch {}
    }
    const visibility = () => { check(); if (!document.hidden) refresh(); };
    const observer = new MutationObserver(() => { clearTimeout(mutationTimer); mutationTimer = setTimeout(check, 80); });
    observer.observe(document.body, {subtree:true,childList:true,attributes:true,attributeFilter:['open','hidden','aria-modal']});
    const scheduleInterval = setInterval(check, 15000), refreshInterval = setInterval(refresh, 90000);
    document.addEventListener('visibilitychange', visibility); window.addEventListener('pageshow', check); window.addEventListener('popstate', visibility);
    check();
    if (data.complete === false) refresh();
    return () => {
      destroyed = true; controller?.abort(); observer.disconnect(); clearTimeout(delayTimer); clearTimeout(mutationTimer); clearInterval(scheduleInterval); clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', visibility); window.removeEventListener('pageshow', check); window.removeEventListener('popstate', visibility);
      dialog.removeEventListener('close', restore); dialog.removeEventListener('click', close); dialog.removeEventListener('click', backdrop);
      if (dialog.open) dialog.close(); announcement.remove(); if (base) hide(base, !!originalBaseHidden);
    };
  }
  const initialize = () => { manager?.(); const root = document.querySelector('[data-seasonal-root]'); manager = root ? start(root) : null; };
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => { if (event.target.querySelector('[data-seasonal-root]')) { manager?.(); manager = null; } });
})();
